import React from 'react'
import { ApprenticeshipFormData, ApprenticeshipFormType } from '../../utils/sharedTypes'
import { Button, Checkbox, FormControl, Grid, InputLabel, MenuItem, Select, Typography, useTheme } from '@mui/material'
import { DropZone, Formtitle, Layout } from '../../components'
import { MdNavigateBefore, MdNavigateNext } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { isApprenticeshipStepValid, formatDateToYYYYMMDD } from '../../utils/misc'
import Bugsnag from '@bugsnag/js'
import { client } from '../../utils/client'
import { commute_transports, travel_times } from '../../utils/constants'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'

const FixedMenuItem: React.FC<React.ComponentProps<typeof MenuItem>> = (props) => (
  <MenuItem style={{ whiteSpace: 'normal' }} {...props} />
)

type ProfilePropsType = {
  form: ApprenticeshipFormType
  setForm: React.Dispatch<React.SetStateAction<ApprenticeshipFormType>>
}

const Profile = ({ form, setForm }: ProfilePropsType) => {
  const theme = useTheme()
  const navigate = useNavigate()

  // Ne pas rendre le composant si les données ne sont pas encore chargées
  if (!form.data.global_id) {
    return <div>Chargement...</div>
  }

  const submitInfo = () => {
    client
      .patch(`/apprenticeship/public/${form.data.global_id}/`, form.data)
      .then((resp) => {
        if (resp.status === 200) {
          navigate({ pathname: './../situation/', search: location.search })
        }
      })
      .catch((err: Error) => {
        Bugsnag.notify(err)
        console.error(err)
      })
  }

  const handleChange = (key: string, value: string | number | boolean | Record<string, string> | null) => {
    setForm((prevState) => {
      if (key.includes('.')) {
        const keys = key.split('.')
        const firstKey = keys.shift() as keyof ApprenticeshipFormData

        let nested = prevState.data[firstKey] as unknown as Record<
          string,
          string | boolean | number | Record<string, string> | null
        >
        keys.forEach((k, i) => {
          if (i === keys.length - 1) {
            nested[k] = value
          } else {
            nested = nested[k] as Record<string, string | boolean | Record<string, string>>
          }
        })
        return {
          ...prevState,
          data: {
            ...prevState.data,
            [firstKey]: nested,
          },
        }
      } else {
        return {
          ...prevState,
          data: {
            ...prevState.data,
            [key]: value,
          },
        }
      }
    })
  }

  return (
    <Layout form={form.data} steps={form.steps}>
      <Formtitle
        title="Votre profil"
        subtitle="Remplissez le formulaire ci-dessous pour poursuivre votre inscription"
      />

      <Grid container spacing={2}>
        <Grid item xs={12} sx={{ width: '100%', mt: 1 }}>
          <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
            Vos possibilités de mobilité
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="commute_transportation">Votre moyen de transport</InputLabel>
                <Select
                  labelId="commute_transportation-label"
                  label="Sélectionner une option"
                  onChange={(e) => {
                    handleChange('commute_transportation', e.target.value || '')
                  }}
                  value={form.data.commute_transportation}
                >
                  {commute_transports.map((ct) => (
                    <FixedMenuItem value={ct.id}>{ct.label}</FixedMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="commute_max_duration">Temps de trajet maximal</InputLabel>
                <Select
                  labelId="commute_max_duration-label"
                  label="Sélectionner une option"
                  onChange={(e) => {
                    handleChange('commute_max_duration', e.target.value || '10')
                  }}
                  value={form.data.commute_max_duration}
                >
                  {travel_times.map((ct) => (
                    <FixedMenuItem value={ct.id}>{ct.label}</FixedMenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item container xs={12} md={12} alignItems="center">
              <FormControl fullWidth>
                <Grid container item alignItems="center" sx={{ flexWrap: 'nowrap' }}>
                  <Checkbox
                    id="ok_to_work_saturday"
                    onChange={(value) => {
                      handleChange('ok_to_work_saturday', value.target.checked)
                    }}
                    value={form.data.ok_to_work_saturday}
                  />
                  <Grid>
                    <Typography variant="body1">
                      Je suis disponible pour travailler le samedi si l’entreprise le requiert
                    </Typography>
                  </Grid>
                </Grid>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ width: '100%', mt: 1 }}>
          <Typography variant="body1" fontWeight="bold" sx={{ mb: 2 }}>
            Quand souhaitez-vous commencer votre alternance ?
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={12}>
              <FormControl fullWidth>
                <DatePicker
                  label="Date de début souhaitée"
                  format="DD/MM/YYYY"
                  onChange={(value) => {
                    console.log(value)
                    // Convertir la Date en format YYYY-MM-DD
                    const formattedDate = formatDateToYYYYMMDD(value)
                    console.log(formattedDate)
                    handleChange('desired_starting_date', formattedDate)
                  }}
                  value={form.data.desired_starting_date ? dayjs(form.data.desired_starting_date) : null}
                />
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ width: '100%', mt: 1 }}>
          <Typography variant="body1" fontWeight="bold" sx={{ mb: 1 }}>
            Déposez votre lettre de motivation{' '}
            <Typography variant="caption">(si vous en avez une - ce n’est pas obligatoire)</Typography>
          </Typography>
          <DropZone
            maxFiles={1}
            maxSize={10}
            sx={{ minHeight: 100, background: theme.palette.background.default }}
            accept={{
              'image/*': ['.png', '.jpeg'],
              'application/pdf': ['.pdf'],
            }}
            bucketName="walter-inscription"
            folderPath={form.data.global_id}
            existingFileUrl={form.data.cover_letter || ''}
            onUploaded={(url: string) => {
              handleChange('cover_letter', url)
            }}
          />
        </Grid>

        <Grid item xs={12} sx={{ width: '100%', mt: 2 }}>
          <Typography
            variant="body1"
            fontWeight="bold"
            sx={{
              mb: 1,
            }}
          >
            Déposez votre CV *
          </Typography>
          <DropZone
            required
            maxFiles={1}
            maxSize={10}
            sx={{ minHeight: 100, background: theme.palette.background.default }}
            accept={{
              'image/*': ['.png', '.jpeg'],
              'application/pdf': ['.pdf'],
            }}
            bucketName="walter-inscription"
            folderPath={form.data.global_id}
            existingFileUrl={form.data.curriculum_vitae || ''}
            onUploaded={(url: string) => {
              handleChange('curriculum_vitae', url)
            }}
          />
        </Grid>
      </Grid>

      <Grid
        item
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          mt: 2,
        }}
      >
        <Button
          variant="outlined"
          sx={{
            height: {
              xs: 45,
            },
          }}
          startIcon={<MdNavigateBefore />}
          onClick={() => {
            if (form.data.has_apprenticeship_company) {
              navigate({ pathname: './../entreprise/', search: location.search })
            } else {
              navigate({ pathname: './../information/', search: location.search })
            }
          }}
        >
          Précédent
        </Button>

        <Button
          variant="contained"
          sx={{
            height: {
              xs: 45,
            },
          }}
          disabled={!isApprenticeshipStepValid('profile', form)}
          endIcon={<MdNavigateNext />}
          onClick={submitInfo}
        >
          Suivant
        </Button>
      </Grid>
    </Layout>
  )
}

export default Profile
